<script setup>
import { useGovernance } from '@/composables/useGovernance'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'

// Module 14 (§9.1) — daftar Kebijakan, sama persis pola governance/sk/
// index.vue, hanya kunci kategorinya berbeda. Lihat CLAUDE.md § Governance.
definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Governance',
  },
})

const ability = useAbility()
const router = useRouter()
const { categories, fetchCategories } = useGovernance()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const fetchItems = async () => {
  if (!categories.value.kebijakan_id) return

  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value, document_category_id: categories.value.kebijakan_id }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/documents', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = response?.meta?.total ?? data?.meta?.total ?? 0
  } catch (e) {
    console.error('Fetch Kebijakan error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchCategories()
  await fetchItems()
})
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const statusColor = status => ({
  draft: 'secondary',
  review: 'warning',
  approved: 'info',
  published: 'success',
  archived: 'secondary',
}[status] ?? 'secondary')

const statusLabel = status => ({
  draft: 'Draft',
  review: 'Review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
}[status] ?? status)

const headers = [
  { title: 'Nomor Kebijakan', key: 'document_number', width: 200 },
  { title: 'Judul', key: 'title' },
  { title: 'Unit', key: 'unit' },
  { title: 'Status', key: 'status', align: 'center', width: 130 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Kebijakan
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Dikelola penuh lewat Document Engine (§9.1)
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Document') && categories.kebijakan_id"
            prepend-icon="ri-add-line"
            @click="router.push({ name: 'document-create', query: { category_id: categories.kebijakan_id } })"
          >
            Buat Kebijakan
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari judul atau nomor kebijakan..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
        @click:row="(e, { item }) => router.push({ name: 'document-id', params: { id: item.id } })"
      >
        <template #item.document_number="{ item }">
          <code>{{ item.document_number }}</code>
        </template>
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.status="{ item }">
          <VChip
            size="small"
            :color="statusColor(item.status)"
            variant="tonal"
          >
            {{ statusLabel(item.status) }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            icon="ri-eye-line"
            variant="text"
            size="small"
            @click.stop="router.push({ name: 'document-id', params: { id: item.id } })"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada kebijakan
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>
  </div>
</template>
